import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  AlertTriangle,
  Users,
  Settings,
  Shield,
  LayoutDashboard,
  ArrowRight,
  LogOut,
  Activity,
  Box,
  Building2,
  Briefcase,
  Bell,
  Inbox,
  HelpCircle,
  Book,
  ClipboardList,
  FileText,
  BarChart3,
  History,
  Folder,
  List,
  Calendar,
  Download,
  Gift,
  UserCheck,
  ShieldBan,
  UserPlus,
  BookOpen,
  LineChart,
  TrendingUp,
  Banknote,
  PiggyBank,
  LifeBuoy,
  ArrowRightLeft,
  Gauge,
  Radar
} from 'lucide-react';
import { toast } from 'sonner';
import NotificationBell from '../components/NotificationBell';
import { VigilanciaHomeCard } from '../components/vigilancia/VigilanciaHomeCard';

// Map icon names to Lucide components
const iconMap = {
  AlertTriangle,
  Users,
  Settings,
  Shield,
  LayoutDashboard,
  Activity,
  Box,
  Building2,
  Briefcase,
  Bell,
  Inbox,
  ClipboardList,
  FileText,
  BarChart3,
  History,
  Folder,
  List,
  Calendar,
  Download,
  Gift,
  UserCheck,
  ShieldBan,
  UserPlus,
  BookOpen,
  LineChart,
  TrendingUp,
  Banknote,
  PiggyBank,
  LifeBuoy,
  ArrowRightLeft,
  Gauge,
  Radar,
};

const ModuleHome = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [fallos, setFallos] = useState([]);
  const [fallosVisible, setFallosVisible] = useState(false);

  // Roles con acceso total
  const ADMIN_ROLES = ['admin', 'superadmin', 'administrador'];
  const isAdmin = user?.rol && ADMIN_ROLES.includes(user.rol.toLowerCase());

  useEffect(() => {
    loadModules();
    loadStats();
    loadFallos();
  }, []);

  const loadModules = async () => {
    try {
      const res = await api.get('/modules/');
      let modulosDisponibles = res.data;
      
      // Si NO es admin, filtrar módulos según permisos
      if (!isAdmin) {
        // Obtener todos los permisos del usuario
        const permisosRes = await api.get('/permisos-v2/mis-permisos');
        const permisosData = permisosRes.data;
        
        // Filtrar módulos donde el usuario tiene al menos una sección con "ver"
        modulosDisponibles = res.data.filter(modulo => {
          const moduloPermisos = permisosData.modulos?.find(m => m.modulo_id === modulo.id);
          
          if (!moduloPermisos) return false;
          
          // Verificar si tiene al menos una sección con permiso de ver
          const tieneAlgunaSeccionVisible = moduloPermisos.secciones?.some(
            s => s.acciones?.ver === true
          );
          
          return tieneAlgunaSeccionVisible;
        });
      }
      
      setModules(modulosDisponibles);
    } catch (error) {
      console.error('Error loading modules:', error);
      toast.error('Error al cargar módulos');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await api.get('/stats/dashboard');
      setStats(res.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadFallos = async () => {
    try {
      const res = await api.get('/waya-movimientos/fallos-bloqueo');
      if (res.data?.visible) {
        setFallos(res.data.rows || []);
        setFallosVisible(true);
      }
    } catch (error) {
      // silencioso: si no es audiencia o falla, no mostramos nada
    }
  };

  const descartarFallo = async (id) => {
    try {
      await api.post('/waya-movimientos/fallos-bloqueo/descartar', { id });
      setFallos((prev) => prev.filter((f) => f.id !== id));
      toast.success('Alerta descartada');
    } catch (error) {
      toast.error('No se pudo descartar la alerta');
    }
  };

  const getModuleSlug = (nombre) => {
    return nombre.toLowerCase()
      .replace(/ /g, '-')
      .replace(/ó/g, 'o')
      .replace(/á/g, 'a')
      .replace(/é/g, 'e')
      .replace(/í/g, 'i')
      .replace(/ú/g, 'u')
      .replace(/ñ/g, 'n');
  };

  const handleModuleClick = (module) => {
    const slug = getModuleSlug(module.nombre);
    // Ir a la BASE del módulo. ModuleLayout redirige automáticamente a la primera
    // sección ACCESIBLE del usuario (según permisos), evitando el "Acceso Denegado"
    // cuando la primera sección global no es a la que el usuario tiene acceso.
    navigate(`/m/${slug}`);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getIcon = (iconName) => {
    const Icon = iconMap[iconName] || Box;
    return Icon;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Cargando módulos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-red-600 rounded-lg">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">AlertGuard</h1>
                <p className="text-xs text-slate-500">Sistema de Monitoreo Transaccional</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/manual')}
                className="bg-blue-50 hover:bg-blue-100 border border-blue-200"
                title="Manual de Usuario"
                data-testid="help-button-home"
              >
                <HelpCircle size={18} className="text-blue-600 mr-2" />
                <span className="text-blue-700 text-sm font-medium hidden sm:inline">Ayuda</span>
              </Button>
              <NotificationBell />
              <div className="text-right">
                <p className="text-sm font-medium text-slate-900">{user?.nombre}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-slate-600 hover:text-red-600"
                data-testid="logout-home-btn"
              >
                <LogOut size={18} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Message */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-slate-900">
            Bienvenido, {user?.nombre?.split(' ')[0]}
          </h2>
          <p className="text-slate-600 mt-1">
            Selecciona un módulo para comenzar
          </p>
        </div>

        {/* Alerta grande: bloqueos/cierres con error en Waya (tras reintentos) */}
        {fallosVisible && fallos.length > 0 && (
          <Card className="mb-8 p-5 border-2 border-red-300 bg-red-50" data-testid="home-fallos-bloqueo">
            <div className="flex items-start gap-3">
              <div className="p-2.5 bg-red-600 rounded-lg shrink-0">
                <ShieldBan className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold text-red-800" data-testid="home-fallos-titulo">
                  {fallos.length} bloqueo(s)/cierre(s) con error en Waya
                </h3>
                <p className="text-sm text-red-700 mb-3">
                  No se pudieron ejecutar tras 3 reintentos automáticos. El sistema los reintentará
                  en las próximas corridas; si se resuelven, la alerta desaparece sola. Revisá y
                  gestioná manualmente si es necesario.
                </p>
                <div className="space-y-1.5 max-h-60 overflow-y-auto">
                  {fallos.map((f) => (
                    <div
                      key={f.id}
                      className="flex flex-wrap items-center gap-2 bg-white rounded-lg border border-red-200 px-3 py-2 text-sm"
                      data-testid={`home-fallo-${f.id}`}
                    >
                      <span className="font-semibold text-red-700 text-xs px-2 py-0.5 rounded bg-red-100">
                        {f.tipo_label}
                      </span>
                      <span className="text-slate-800 font-medium truncate max-w-[220px]">
                        {f.nombre || f.titulo || 'Caso'}
                      </span>
                      <span className="font-mono text-xs text-slate-500">
                        {f.cuit || (f.cvus && f.cvus[0]) || ''}
                      </span>
                      <span className="text-xs text-slate-400 truncate hidden md:inline max-w-[280px]">
                        {f.detalle}
                      </span>
                      <div className="ml-auto flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-red-700 hover:text-red-800"
                          onClick={() => navigate('/m/clientes/waya-movimientos')}
                          data-testid={`home-fallo-ir-${f.id}`}
                        >
                          Ir al Monitor
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 border-red-300 text-red-700 hover:bg-red-100"
                          onClick={() => descartarFallo(f.id)}
                          data-testid={`home-fallo-descartar-${f.id}`}
                        >
                          Descartar
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}

        <VigilanciaHomeCard />

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4 bg-white border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Alertas Pendientes</p>
                <p className="text-2xl font-bold text-red-600">{stats.alertas_nuevas || 0}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-200" />
            </div>
          </Card>
          <Card className="p-4 bg-white border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">En Investigación</p>
                <p className="text-2xl font-bold text-blue-600">{stats.alertas_en_investigacion || 0}</p>
              </div>
              <Activity className="h-8 w-8 text-blue-200" />
            </div>
          </Card>
          <Card className="p-4 bg-white border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Monitores Activos</p>
                <p className="text-2xl font-bold text-green-600">{stats.monitores_activos || 0}</p>
              </div>
              <Shield className="h-8 w-8 text-green-200" />
            </div>
          </Card>
          <Card className="p-4 bg-white border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Clientes</p>
                <p className="text-2xl font-bold text-purple-600">{stats.total_clientes || 0}</p>
              </div>
              <Users className="h-8 w-8 text-purple-200" />
            </div>
          </Card>
        </div>

        {/* Modules Grid */}
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Módulos Disponibles</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {modules.filter(m => m.activo).map((module) => {
            const Icon = getIcon(module.icono);
            const sectionCount = module.secciones?.length || 0;
            
            return (
              <Card
                key={module.id}
                className="p-6 bg-white border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all cursor-pointer group"
                onClick={() => handleModuleClick(module)}
                data-testid={`module-card-${getModuleSlug(module.nombre)}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div 
                    className="p-3 rounded-xl"
                    style={{ backgroundColor: `${module.color}20` }}
                  >
                    <Icon 
                      className="h-6 w-6" 
                      style={{ color: module.color }}
                    />
                  </div>
                  <ArrowRight 
                    className="h-5 w-5 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-1 transition-all" 
                  />
                </div>
                
                <h4 className="text-lg font-semibold text-slate-900 mb-1">
                  {module.nombre}
                </h4>
                <p className="text-sm text-slate-600 mb-4">
                  {module.descripcion || 'Sin descripción'}
                </p>
                
                <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                  <span className="text-xs text-slate-400">
                    {sectionCount} secciones
                  </span>
                  <div className="flex -space-x-1">
                    {module.secciones?.slice(0, 4).map((section, idx) => {
                      const SectionIcon = getIcon(section.icono);
                      return (
                        <div
                          key={idx}
                          className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center border border-white"
                          title={section.nombre}
                        >
                          <SectionIcon className="h-3 w-3 text-slate-700" />
                        </div>
                      );
                    })}
                    {sectionCount > 4 && (
                      <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[10px] text-slate-600 border border-white">
                        +{sectionCount - 4}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {modules.length === 0 && (
          <Card className="p-8 bg-white text-center">
            <Box className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <h4 className="text-lg font-medium text-slate-700">No hay módulos disponibles</h4>
            <p className="text-sm text-slate-500 mt-1">
              Contacte al administrador para configurar los módulos del sistema
            </p>
          </Card>
        )}
      </main>

      {/* Footer */}
      <footer className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 mt-8">
        <div className="text-center text-xs text-slate-400">
          AlertGuard © {new Date().getFullYear()} - Sistema de Monitoreo Transaccional
        </div>
      </footer>
    </div>
  );
};

export default ModuleHome;
